import { Button } from "@meiden-connect/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  isMcpToolVisible,
  MCP_TOOLS_OVERVIEW,
  type McpCanvasPermissions,
  type McpToolPermission,
} from "@refinement-canvas/domain";
import { getWebConfig } from "../../env";
import { useCanvasApi } from "../../lib/canvas-api";
import { navigate } from "../../router";

// MCP（リモート Model Context Protocol）連携の設定手順ページ。
// AI エージェント（Claude.ai / Claude Code / Cursor 等）からキャンバスを検索・分析・編集する
// ための接続情報と手順を案内する。

/** MCP エンドポイント URL を組み立てる（serviceOrigin 優先、無ければ現在のオリジン）。 */
function buildMcpUrl(): string {
  const config = getWebConfig();
  const origin = (config.serviceOrigin ?? window.location.origin).replace(/\/+$/u, "");
  return `${origin}/mcp`;
}

const PERMISSION_LABEL: Record<McpToolPermission, string> = {
  any: "全員",
  read: "閲覧",
  write: "編集",
  admin: "管理",
};

export function McpSetupView(): JSX.Element {
  const mcpUrl = useMemo(() => buildMcpUrl(), []);
  const canvasApi = useCanvasApi();

  const meQuery = useQuery({ queryKey: ["me"], queryFn: canvasApi.fetchMe });
  const permissions: McpCanvasPermissions = meQuery.data?.actor.permissions ?? {
    canRead: false,
    canWrite: false,
    canAdmin: false,
  };
  const visibleTools = MCP_TOOLS_OVERVIEW.filter((tool) => isMcpToolVisible(tool, permissions));

  const claudeCodeCommand = `claude mcp add --transport http refinement-canvas ${mcpUrl}`;
  const clientConfigJson = JSON.stringify(
    {
      mcpServers: {
        "refinement-canvas": {
          type: "streamable-http",
          url: mcpUrl,
          // OAuth 自動認証を使わない場合のみ、API キー / アクセストークンを指定する。
          headers: { Authorization: "Bearer YOUR_API_KEY_OR_ACCESS_TOKEN" },
        },
      },
    },
    null,
    2,
  );

  return (
    <div className="dashboard-content" data-testid="mcp-setup-root">
      <section className="dashboard-hero-card mcp-hero">
        <div className="dashboard-hero-copy">
          <p className="dashboard-eyebrow">MCP 連携</p>
          <h1>AI からキャンバスを操作する（MCP 設定手順）</h1>
          <p>
            MCP（Model Context Protocol）対応の AI エージェント（Claude.ai / Claude Code /
            Cursor など）をリファインメントキャンバスに接続すると、自然言語で付箋・テキストを含む
            キャンバスの検索・分析や、新規キャンバスの作成・既存キャンバスへの図形コンテンツ追加が
            行えます。
          </p>
          <div>
            <Button type="button" variant="action" onClick={() => navigate("/")}>
              ← ダッシュボードへ戻る
            </Button>
          </div>
        </div>
      </section>

      <section className="dashboard-card">
        <h2>接続情報</h2>
        <dl className="mcp-info-grid">
          <dt>エンドポイント URL</dt>
          <dd>
            <CopyableCode value={mcpUrl} />
          </dd>
          <dt>トランスポート</dt>
          <dd>Streamable HTTP（MCP プロトコル 2025-03-26）</dd>
          <dt>認証方式</dt>
          <dd>OAuth 認証（推奨）／ アクセストークン ／ API キー</dd>
        </dl>
        <p className="dashboard-muted mcp-note">
          ※ MCP 経由で追加・変更した内容は、すでにキャンバスを開いている画面には即時反映されません。
          反映を確認するにはブラウザでキャンバスを再読み込みしてください。
        </p>
      </section>

      <section className="dashboard-card">
        <h2>Claude Code で接続する（OAuth 自動認証）</h2>
        <p>
          ターミナルで次のコマンドを実行し、ブラウザで MEIDEN CONNECT にログインするだけで接続できます。
        </p>
        <CopyableCode value={claudeCodeCommand} block />
        <p className="dashboard-muted">
          初回はブラウザが開き、MEIDEN CONNECT の認可画面が表示されます。許可するとトークンが自動で
          保存され、以降は再認証なしで利用できます。
        </p>
      </section>

      <section className="dashboard-card">
        <h2>Claude.ai（ブラウザ版）で接続する</h2>
        <ol className="mcp-steps">
          <li>Claude.ai の設定 →「コネクタ」→「カスタムコネクタを追加」を開きます。</li>
          <li>
            「リモート MCP サーバーの URL」に上記のエンドポイント URL（
            <code className="mcp-inline-code">{mcpUrl}</code>）を入力します。
          </li>
          <li>追加後に「接続」を押すと MEIDEN CONNECT のログイン画面が開きます。許可すると接続完了です。</li>
        </ol>
      </section>

      <section className="dashboard-card">
        <h2>その他のクライアント（Claude Desktop / Cursor / Codex / GitHub Copilot 等）</h2>
        <p>
          設定ファイルに次の MCP サーバー定義を追加します。OAuth 自動認証に対応しないクライアントでは、
          <code className="mcp-inline-code">Authorization</code> ヘッダーに API キーまたはアクセストークンを
          設定してください。
        </p>
        <CopyableCode value={clientConfigJson} block />
      </section>

      <section className="dashboard-card">
        <h2>認証方式の選び方</h2>
        <ul className="mcp-auth-list">
          <li>
            <strong>OAuth 認証（推奨）</strong>：Claude.ai / Claude Code など対応クライアントは、URL を
            登録するだけで MEIDEN CONNECT のログインに誘導され、トークン管理も自動です。
          </li>
          <li>
            <strong>アクセストークン</strong>：短期間の動作確認向け。ブラウザの開発者ツールで取得した
            アクセストークンを <code className="mcp-inline-code">Authorization: Bearer &lt;token&gt;</code> に
            設定します（有効期限が切れると再取得が必要です）。
          </li>
          <li>
            <strong>API キー</strong>：長期運用向け。MEIDEN CONNECT で発行した API キーを Bearer に設定すると、
            サーバー側で短命アクセストークンへ自動交換します。
          </li>
        </ul>
      </section>

      <section className="dashboard-card">
        <h2>利用できるツール</h2>
        {meQuery.isLoading ? (
          <p className="dashboard-muted">読み込み中…</p>
        ) : (
          <>
            <p className="dashboard-muted">
              現在のログイン権限（閲覧 {labelBool(permissions.canRead)} / 編集{" "}
              {labelBool(permissions.canWrite)} / 管理 {labelBool(permissions.canAdmin)}）で利用できる
              ツールの一覧です。
            </p>
            <table className="mcp-tools-table">
              <thead>
                <tr>
                  <th>ツール名</th>
                  <th>説明</th>
                  <th>必要権限</th>
                </tr>
              </thead>
              <tbody>
                {visibleTools.map((tool) => (
                  <tr key={tool.name}>
                    <td>
                      <code className="mcp-inline-code">{tool.name}</code>
                    </td>
                    <td>{tool.summary}</td>
                    <td>{PERMISSION_LABEL[tool.permission]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </section>

      <section className="dashboard-card">
        <h2>基本的な使い方の例</h2>
        <ol className="mcp-steps">
          <li>「最近のリファインメントのキャンバスから『在庫管理』に関するものを探して」→ search_canvases</li>
          <li>「そのキャンバスの付箋を要約して」→ get_canvas で内容を取得し AI が要約</li>
          <li>「スプリント計画用の新しいキャンバスを作って、優先度の付箋を3つ置いて」→ create_canvas</li>
          <li>「このキャンバスに『リスク』の付箋と、それを指す矢印を追加して」→ add_canvas_objects</li>
        </ol>
      </section>
    </div>
  );
}

function labelBool(value: boolean): string {
  return value ? "○" : "×";
}

/** クリップボードへコピーできるコード表示。block=true で複数行ブロック表示にする。 */
function CopyableCode({
  value,
  block,
}: {
  readonly value: string;
  readonly block?: boolean | undefined;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const handleCopy = (): void => {
    void navigator.clipboard?.writeText(value).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      },
      () => {
        // クリップボードが使えない環境では何もしない（手動コピーにフォールバック）。
      },
    );
  };
  return (
    <div className={block ? "mcp-code-block" : "mcp-code-inline-row"}>
      <code className={block ? "mcp-code-block-text" : "mcp-inline-code"}>{value}</code>
      <Button type="button" variant="action" onClick={handleCopy}>
        {copied ? "コピーしました" : "コピー"}
      </Button>
    </div>
  );
}
